/**
 * UploadSignedAgreementModal — record a proposal as executed from a signed hard copy the client
 * returned OUTSIDE the app (printed, signed by hand, scanned, emailed back) instead of e-signing
 * in the Client Hub. See api/proposal-manual-execute.js and services/proposals.js's
 * recordProposalExecutedManually. Opened from CrmProposalDetailModal's "Signed outside the app…"
 * button — same principal-only authority as Countersign, since the consequences are identical
 * (fires the same onboarding kickoff, locks the contract).
 */
import { useState } from 'react'
import CrmModal, { F } from './CrmModal.jsx'
import { recordProposalExecutedManually } from '../../services/proposals.js'
import { alertDialog, confirmDialog } from '../Dialog.jsx'

const PACKAGE_LABEL = { A: 'Essential Design', B: 'Premier Architecture', C: 'Architecture + Interiors' }

export default function UploadSignedAgreementModal({ proposal: p, onClose, onDone }) {
  const [file, setFile] = useState(null)
  const [selectedPackage, setSelectedPackage] = useState('')
  const [busy, setBusy] = useState(false)

  const packageKeys = ['A', 'B', 'C'].filter(k => p.packages && p.packages[k])
  const needsPackage = !!p.tiered && packageKeys.length > 1

  async function submit() {
    if (!file) { await alertDialog({ title: 'Choose a file', message: 'Select the signed PDF before continuing.' }); return }
    if (needsPackage && !selectedPackage) { await alertDialog({ title: 'Select a suite', message: 'Choose which package the client signed.' }); return }
    const ok = await confirmDialog({
      title: 'Record as executed?',
      message: 'This marks the agreement as fully executed and will kick off project onboarding, exactly like an in-app countersignature. This cannot be undone.',
      confirmLabel: 'Record as executed',
    })
    if (!ok) return
    setBusy(true)
    try {
      await recordProposalExecutedManually(p.id, file, needsPackage ? { selectedPackage } : {})
      await alertDialog({ title: 'Recorded', message: 'The agreement is now fully executed.' })
      onDone && onDone()
      onClose()
    } catch (e) {
      await alertDialog({ title: 'Could not record execution', message: e.message })
    } finally { setBusy(false) }
  }

  return (
    <CrmModal title="Signed outside the app" onClose={onClose} width={480}
      footer={<>
        <button style={F.btnGhost} onClick={onClose} disabled={busy}>Cancel</button>
        <button style={F.btnPrimary} onClick={submit} disabled={busy}>{busy ? 'Recording…' : 'Record as executed'}</button>
      </>}>
      <p style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, color: 'var(--aj-text-mid)', margin: '0 0 14px', lineHeight: 1.5 }}>
        For a client who printed, signed by hand, and returned the agreement outside the app. Upload
        the scanned, fully-signed PDF — it becomes the record of the executed agreement.
      </p>
      <div style={F.row}>
        <label style={F.label}>Signed agreement (PDF)</label>
        <input type="file" accept="application/pdf" style={F.input}
          onChange={e => setFile(e.target.files && e.target.files[0] ? e.target.files[0] : null)} disabled={busy} />
      </div>
      {needsPackage && (
        <div style={F.row}>
          <label style={F.label}>Suite the client signed</label>
          <select style={F.input} value={selectedPackage} onChange={e => setSelectedPackage(e.target.value)} disabled={busy}>
            <option value="">Select…</option>
            {packageKeys.map(k => <option key={k} value={k}>{PACKAGE_LABEL[k] || k}</option>)}
          </select>
        </div>
      )}
    </CrmModal>
  )
}
